import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { RouteError, RouteNotFound } from "@/components/route-error";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  extractFromText,
  commitCapture,
  type CaptureExtractResult,
} from "@/lib/capture.functions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  CaptureReview,
  itemsFromExtract,
  payloadFromItems,
  type EditableItem,
} from "@/components/capture-review";

export const Route = createFileRoute("/_authenticated/capture")({
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: CapturePage,
});

function CapturePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const extract = useServerFn(extractFromText);
  const commit = useServerFn(commitCapture);

  const [text, setText] = useState("");
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [phase, setPhase] = useState<"compose" | "review">("compose");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CaptureExtractResult | null>(null);
  const [items, setItems] = useState<EditableItem[]>([]);

  // Load draft from /share
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("aria.capture.draft");
      if (raw) {
        const draft = JSON.parse(raw) as { text?: string; url?: string | null };
        if (draft.text) setText(draft.text.slice(0, 10000));
        if (draft.url) setSourceUrl(draft.url);
        sessionStorage.removeItem("aria.capture.draft");
      }
    } catch {
      // ignore
    }
  }, []);

  const onExtract = async () => {
    if (text.trim().length < 2) return;
    setBusy(true);
    try {
      const out = await extract({ data: { text: text.trim(), url: sourceUrl } });
      setResult(out);
      setItems(itemsFromExtract(out));
      setPhase("review");
      if (out.error) toast.error(out.error);
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't extract.");
    } finally {
      setBusy(false);
    }
  };

  const onSave = async () => {
    const payload = payloadFromItems(items);
    if (!payload.length) {
      toast.error("Nothing to save.");
      return;
    }
    setBusy(true);
    try {
      const res = await commit({ data: { items: payload, sourceUrl } });
      qc.invalidateQueries({ queryKey: ["home-digest"] });
      qc.invalidateQueries({ queryKey: ["people"] });
      const total = res.created + res.updated;
      toast.success(
        res.created && res.updated
          ? `Saved ${res.created} new, updated ${res.updated}.`
          : res.created
            ? `Added ${res.created} ${res.created === 1 ? "person" : "people"}.`
            : `Updated ${res.updated} ${res.updated === 1 ? "contact" : "contacts"}.`,
      );
      if (res.contactIds.length === 1) {
        navigate({ to: "/contacts/$contactId", params: { contactId: res.contactIds[0] } });
      } else {
        navigate({ to: "/people" });
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl pb-32">
      <header className="flex items-center justify-between gap-2 py-3">
        <Link
          to="/today"
          className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <Sparkles className="h-4 w-4 text-primary" /> Capture
        </div>
        <div className="w-12" />
      </header>

      {phase === "compose" && (
        <section className="space-y-3 px-1">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Paste anything about a person
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Email signatures, a LinkedIn intro, a text thread, freeform notes — Pyng will
              extract who, what, and what to remember.
            </p>
          </div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 10000))}
            placeholder="Paste here…"
            className="min-h-[260px] resize-none text-base"
            autoFocus
          />
          <div className="flex items-center justify-between text-[14px] text-muted-foreground">
            <span>{text.length} / 10,000</span>
            {sourceUrl && (
              <span className="truncate max-w-[60%]">From: {sourceUrl}</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onExtract}
              disabled={busy || text.trim().length < 2}
              className="flex-1"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Extract"}
            </Button>
            <Button variant="ghost" onClick={() => navigate({ to: "/today" })} disabled={busy}>
              Cancel
            </Button>
          </div>
        </section>
      )}

      {phase === "review" && (
        <section className="space-y-3 px-1">
          {items.length === 0 ? (
            <div className="rounded-2xl border border-border/60 bg-card p-6 text-center">
              <p className="text-sm text-muted-foreground">
                Couldn't find anyone. You can paste again or pick a contact to attach this as
                a note.
              </p>
              <div className="mt-4 flex justify-center gap-2">
                <Button variant="ghost" onClick={() => setPhase("compose")}>
                  Edit text
                </Button>
                <Button asChild>
                  <Link to="/people">Pick contact</Link>
                </Button>
              </div>
            </div>
          ) : (
            <CaptureReview items={items} onChange={setItems} />
          )}

          {result?.error && (
            <p className="text-[14px] text-destructive">{result.error}</p>
          )}

          <div
            className="fixed inset-x-0 bottom-0 z-30 border-t border-border/60 bg-background/95 px-4 py-3 backdrop-blur"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}
          >
            <div className="mx-auto flex max-w-2xl gap-2">
              <Button
                variant="ghost"
                onClick={() => setPhase("compose")}
                disabled={busy}
              >
                Back
              </Button>
              <Button onClick={onSave} disabled={busy} className="flex-1">
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  `Save ${items.filter((i) => !i.skip).length} ${
                    items.filter((i) => !i.skip).length === 1 ? "person" : "people"
                  }`
                )}
              </Button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}